import React, { useEffect, useState } from "react";
import {
  usePincodeDetails,
  useCheckPincodeServiceability,
} from "../../../../../api/orders/orders";
import {
  notifyError,
  notifySuccess,
} from "../../../../../utils/ToastifyProvider";

const ShippingInformation = ({ onPrevious, onNext, prevData }) => {
  const [orderData, setOrderData] = useState({
    ...prevData,
    shippingInfo: {
      CustomerName: "",
      mobile: "",
      email: "",
      address: "",
      address2: "",
      Pincode: "",
      country: "",
      state: "",
      city: "",
      sameAsShipping: false,
      ...(prevData?.shippingInfo || {}),
    },
  });

  const [validationErrors, setValidationErrors] = useState({});
  const { shippingInfo } = orderData;

  const validPincode =
    shippingInfo.Pincode?.length === 6 ? shippingInfo.Pincode : null;

  const { data: pincodeData, isFetching: loadingPincode } =
    usePincodeDetails(validPincode);
  const { data: serviceabilityData } =
    useCheckPincodeServiceability(validPincode);

  const capitalize = (str) =>
    str
      ? str
          .split(" ")
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase())
          .join(" ")
      : "";

  useEffect(() => {
    if (pincodeData) {
      setOrderData((prev) => ({
        ...prev,
        shippingInfo: {
          ...prev.shippingInfo,
          city: capitalize(pincodeData.city),
          state: capitalize(pincodeData.state),
          country: capitalize(pincodeData.country),
        },
      }));
    }
  }, [pincodeData]);
  useEffect(() => {
    if (
      validPincode &&
      serviceabilityData &&
      serviceabilityData.isServiceable === false
    ) {
      notifyError(serviceabilityData.message || "Pincode is not serviceable");
    }
  }, [serviceabilityData, validPincode]);

  useEffect(() => {
    if (shippingInfo.Pincode.length !== 6) {
      setOrderData((prev) => ({
        ...prev,
        shippingInfo: {
          ...prev.shippingInfo,
          city: "",
          state: "",
          country: "",
        },
      }));
    }
  }, [shippingInfo.Pincode]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setOrderData((prevData) => ({
      ...prevData,
      shippingInfo: {
        ...prevData.shippingInfo,
        [name]: value,
      },
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[name];
        return updated;
      });
    }
  };

  const validateFields = () => {
    const errors = {};
    const fields = [
      { key: "CustomerName", label: "Customer Name" },
      { key: "mobile", label: "Mobile Number" },
      { key: "address", label: "Address" },
      { key: "Pincode", label: "Pincode" },
      { key: "country", label: "Country" },
      { key: "state", label: "State" },
      { key: "city", label: "City" },
    ];

    fields.forEach((field) => {
      const value = shippingInfo[field.key]?.trim();
      if (!value) errors[field.key] = `${field.label} is required`;
      if (field.key === "mobile") {
        const isValidMobile = /^\d{10}$/.test(value); // allows only 10 digits
        if (!isValidMobile) {
          errors[field.key] =
            "Mobile Number must be exactly 10 digits and numeric only";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleGoBack = () => onPrevious();
  const handleNext = () => {
    if (validateFields()) {
      onNext(orderData);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white shadow rounded-md">
      <h2 className="text-base font-semibold mb-6">Shipping Information</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Customer Name */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> Customer Name
          </label>
          <input
            type="text"
            name="CustomerName"
            value={shippingInfo.CustomerName}
            onChange={handleChange}
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.CustomerName ? "border-red-500" : ""
            }`}
          />
          {validationErrors.CustomerName && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.CustomerName}
            </p>
          )}
        </div>

        {/* Mobile Number */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> Mobile Number
          </label>
          <div className="flex">
            <span className="px-4 py-2 border rounded-l bg-gray-100">+91</span>
            <input
              type="text"
              name="mobile"
              value={shippingInfo.mobile}
              onChange={(e) => {
                const onlyNumbers = e.target.value.replace(/\D/g, ""); // remove non-digits
                handleChange({
                  target: { name: "mobile", value: onlyNumbers },
                });
              }}
              maxLength={10}
              className={`w-full border rounded-r px-4 py-2 ${
                validationErrors.mobile ? "border-red-500" : ""
              }`}
            />
          </div>
          {validationErrors.mobile && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.mobile}
            </p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm mb-1">Customer Email</label>
          <input
            type="email"
            name="email"
            value={shippingInfo.email}
            onChange={handleChange}
            className="w-full border rounded px-4 py-2"
          />
        </div>

        {/* Address */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> Address
          </label>
          <textarea
            name="address"
            value={shippingInfo.address}
            onChange={handleChange}
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.address ? "border-red-500" : ""
            }`}
          />
          {validationErrors.address && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.address}
            </p>
          )}
        </div>

        {/* Address 2 */}
        <div>
          <label className="block text-sm mb-1">Address 2 (optional)</label>
          <textarea
            name="address2"
            value={shippingInfo.address2}
            onChange={handleChange}
            className="w-full border rounded px-4 py-2"
          />
        </div>

        {/* Pincode */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> Pincode
          </label>
          <input
            type="text"
            name="Pincode"
            value={shippingInfo.Pincode}
            onChange={handleChange}
            maxLength={6}
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.Pincode ? "border-red-500" : ""
            }`}
          />
          {validationErrors.Pincode && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.Pincode}
            </p>
          )}
        </div>

        {/* Country */}

        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> Country
          </label>
          <input
            type="text"
            name="country"
            value={shippingInfo.country}
            onChange={handleChange} // ✅ Now editable
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.country ? "border-red-500" : "border-gray-300"
            }`}
          />
          {validationErrors.country && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.country}
            </p>
          )}
        </div>

        {/* State */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> State
          </label>
          <input
            type="text"
            name="state"
            value={shippingInfo.state}
            onChange={handleChange} // ✅ Now editable
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.state ? "border-red-500" : "border-gray-300"
            }`}
          />
          {validationErrors.state && (
            <p className="text-red-500 text-sm mt-1">
              {validationErrors.state}
            </p>
          )}
        </div>

        {/* City */}
        <div>
          <label className="block text-sm mb-1">
            <span className="text-red-500">*</span> City
          </label>
          <input
            type="text"
            name="city"
            value={shippingInfo.city}
            onChange={handleChange} // ✅ Now editable
            className={`w-full border rounded px-4 py-2 ${
              validationErrors.city ? "border-red-500" : "border-gray-300"
            }`}
          />
          {validationErrors.city && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.city}</p>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="flex justify-end gap-4 mt-6">
        <button
          className="bg-white hover:text-primary border hover:border-primary text-black px-4 py-2 rounded"
          onClick={handleGoBack}
        >
          Previous
        </button>
        <button
          onClick={handleNext}
          className="hover:bg-primaryLight bg-primary hover:text-primary border border-primary text-white px-4 py-2 rounded"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ShippingInformation;
